import * as Pipeline from '../core/utils/pipeline';
import { Component, Element, Event, EventEmitter, Fragment, Method, Prop, State, VNode, Watch, h } from '@stencil/core';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { CheckBlurEvent, CheckBlurHandler, CheckFocusEvent, CheckFocusHandler, CheckToggleEvent, CheckToggleHandler } from './ads-checkbox.types';
import { Positions, Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { generateSVGImage, getIsElementRTL, isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'utils/index';
import { height, svgPathData, width } from '@fortawesome/pro-regular-svg-icons/faTimes';

/**
 * Checkboxes are used to toggling single or multiple options on or off. The checkbox list contains options that are not mutually exclusive, and the default state should be unchecked. The text labels on check boxes are sentence case capitalized.
 * @tag `<ads-checkbox>`
 * @slot - Content rendered as the label text
 * @example
 * <ads-checkbox>Default</ads-checkbox>
 * <ads-checkbox checked>Checked</ads-checkbox>
 * <ads-checkbox disabled>Disabled</ads-checkbox>
 * <ads-checkbox checked disabled>Checked + Disabled</ads-checkbox>
 * <ads-checkbox readonly>Readonly</ads-checkbox>
 * <ads-checkbox checked readonly>Checked + Readonly</ads-checkbox>
 * <ads-checkbox class="is-reversed">Reversed</ads-checkbox>
 * <ads-checkbox class="is-small">Small</ads-checkbox>
 */
@Component({
  tag: 'ads-checkbox',
  styleUrl: 'ads-checkbox.scss',
  shadow: true,
})
export class AdsCheckbox implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-checkbox'));
    this.component = 'ads-checkbox';
  }

  @Element() private el: HTMLAdsCheckboxElement;

  /**
   * @hidden
   */
  @State() isCheckedInternal = false;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * Whether or not the checkbox is checked (optional)
   */
  @Prop({ reflect: true }) checked?: boolean;

  /**
   * Whether or not the checkbox is readonly (optional)
   * @default false
   */
  @Prop({ reflect: true }) readonly = false;

  /**
   * Whether or not the checkbox is required (optional)
   * @default false
   */
  @Prop({ reflect: true }) required = false;

  /**
   * The checkbox label (optional)
   */
  @Prop({ reflect: true }) label?: string;

  /**
   * The value for the aria-label of the checkbox, only used if the `label` property is not provided (optional)
   */
  @Prop({ reflect: true }) srLabel?: string;

  /**
   * The checkbox name (optional)
   */
  @Prop({ reflect: true }) name?: string;

  /**
   * The checkbox tooltip (optional)
   */
  @Prop({ reflect: true }) tooltip?: string;

  /**
   * The checkbox helper text (optional)
   */
  @Prop({ reflect: true }) helperText?: string;

  /**
   * Whether or not the toggle is disabled (optional)
   * @default false
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * Callback function triggered on checkbox change (optional)<br/>
   * Usage: `document.querySelector('ads-checkbox').checkToggleHandler = ({evt: Event, checked: boolean}) => {};`
   */
  @Prop() checkToggleHandler?: CheckToggleHandler;

  /**
   * Callback function triggered on checkbox focus (optional)<br/>
   * Usage: `document.querySelector('ads-checkbox').checkFocusHandler = ({evt: Event, id: string}) => {};`
   */
  @Prop() checkFocusHandler?: CheckFocusHandler;

  /**
   * Emits the `checkToggle` custom event.<br/>
   * Usage: `document.querySelector('ads-checkbox').addEventListener('checkToggle', function({evt: Event, checked: boolean}) { });`
   * @event
   */
  @Event({ bubbles: false }) checkToggle: EventEmitter<CheckToggleEvent>;

  /**
   * Emits the `checkBlur` custom event.<br/>
   * Usage: `document.querySelector('ads-checkbox').addEventListener('checkBlur', function({evt: Event, id: string}) { });`
   * @event
   */
  @Event({ bubbles: false }) checkBlur: EventEmitter<CheckBlurEvent>;

  /**
   * Callback function triggered on checkbox blur
   * Usage: document.querySelector('ads-checkbox').checkBlurHandler = ({evt: Event, id: string}) => {};`
   */
  @Prop() checkBlurHandler?: CheckBlurHandler;

  /**
   * Emits the `checkFocus` custom event.<br/>
   * Usage: `document.querySelector('ads-checkbox').addEventListener('checkFocus', function(evt, id) { });`
   * @event
   */
  @Event({ bubbles: false }) checkFocus: EventEmitter<CheckFocusEvent>;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-checkbox'));
  }

  /**
   * Watch for checked prop change
   * @param {boolean}   newValue  The new value for 'checked'
   * @private
   * @hidden
   */
  @Watch('checked')
  onCheckedChange(newValue: boolean): void {
    if (typeof newValue === 'boolean') {
      this.setChecked(newValue);
    }
  }

  /**
   * Returns value of checkbox
   * @return {Promise<boolean>}  The checkbox value
   */
  @Method()
  value(): Promise<boolean> {
    return Promise.resolve(this.isCheckedInternal);
  }

  /**
   * Sets internal state of checkbox
   * @param {boolean} checked the checkbox state
   */
  private setChecked = (checked: boolean): void => {
    this.isCheckedInternal = checked;
  };

  /**
   * Handle change events
   * @param {Event} evt   The original change event
   * @return {void}
   * @private
   * @hidden
   */
  private handleCheckboxChange = (evt: Event): void => {
    if (this.readonly || this.disabled) {
      return;
    }
    this.checkToggle.emit({ evt, checked: !this.isCheckedInternal });

    if (this.checkToggleHandler !== undefined) {
      this.checkToggleHandler({ evt, checked: !this.isCheckedInternal });
    }
    if (typeof this.checked !== 'boolean') {
      this.setChecked(!this.isCheckedInternal);
    }
  };

  /**
   * Handle focus events
   * @param {FocusEvent}   evt   The original focus event
   * @return {void}
   * @private
   * @hidden
   */
  private handleCheckboxFocus = (evt: FocusEvent): void => {
    if (this.disabled) {
      return;
    }
    this.checkFocus.emit({ evt, id: this.identifier });
    if (this.checkFocusHandler !== undefined) {
      this.checkFocusHandler({ evt, id: this.identifier });
    }
  };

  /**
   * Handle blur events
   * @param {Event} evt   The original blur event
   * @return {void}
   * @private
   * @hidden
   */
  private handleCheckboxBlur = (evt: FocusEvent): void => {
    if (this.disabled) {
      return;
    }
    this.checkBlur.emit({ evt, id: this.identifier });
    if (this.checkBlurHandler !== undefined) {
      this.checkBlurHandler({ evt, id: this.identifier });
    }
  };

  /**
   * Handle key up events
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/checkbox_role#Keyboard_interactions
   * @param  {KeyboardEvent}    evt   The original keyboard event
   * @return {void}
   * @private
   * @hidden
   */
  private handleCheckboxKeyDown = (evt: KeyboardEvent): void => {
    if (evt.key === 'Enter' || evt.key === ' ') {
      evt.preventDefault();
      this.handleCheckboxChange(evt);
    }
  };

  protected componentWillLoad(): void {
    if (this.checked === true || this.checked === false) {
      this.setChecked(this.checked);
    }
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  /**
   * Handle rendering the input element
   * @returns {HTMLElement} the input element for the checkbox
   */
  private renderInputElement(): HTMLElement {
    const inputAttributes: {
      id: string;
      name: string;
      tabIndex: number;
      'aria-hidden': string;
      checked?: boolean;
      disabled?: boolean;
      readonly?: boolean;
      required?: boolean;
    } = {
      id: `ads:checkbox:${this.identifier}:input`,
      name: this.name,
      tabIndex: -1,
      'aria-hidden': 'true',
    };

    if (this.isCheckedInternal) {
      inputAttributes['checked'] = true;
    }
    if (this.disabled) {
      inputAttributes['disabled'] = true;
    }
    if (this.readonly) {
      inputAttributes['readonly'] = true;
    }
    if (this.required) {
      inputAttributes['required'] = true;
    }

    return (<input {...inputAttributes} class="c-checkbox__input" type="checkbox" />) as HTMLElement;
  }

  /**
   * Handle rendering the label and cross elements
   * @param {boolean} isReversed Whether the content is reversed
   * @returns {VNode} the label and cross elements for the checkbox
   */
  private renderContentContainer(isReversed: boolean): VNode {
    const checkboxClasses = ['c-checkbox__box'];
    if (isReversed) {
      checkboxClasses.push('is-reversed');
    }
    if (this.isCheckedInternal) {
      checkboxClasses.push('is-checked');
    }
    if (this.disabled) {
      checkboxClasses.push('is-disabled');
    }
    if (this.readonly) {
      checkboxClasses.push('is-readonly');
    }
    if (this.el.classList.contains('is-small')) {
      checkboxClasses.push('is-small');
    }
    const labelText = !isNullOrUndefinedOrEmpty(this.label) ? this.label : this.el.childNodes.length > 0 ? ((<slot />) as HTMLElement) : null;
    const labelClasses = ['c-checkbox__label'];
    if (this.required) {
      labelClasses.push('is-required');
    }

    // Generate the label
    const labelAttributes = {
      tabIndex: this.disabled ? -1 : 0,
      role: 'checkbox',
      'aria-checked': this.isCheckedInternal.toString(),
      'aria-labelledby': !isNullOrUndefined(labelText) ? `ads:checkbox:${this.identifier}:label` : null,
      'aria-label': isNullOrUndefined(labelText) ? (!isNullOrUndefined(this.srLabel) ? this.srLabel : null) : null,
      'aria-readonly': this.readonly.toString(),
      'aria-required': this.required.toString(),
    };

    const $contentEls = [
      !isNullOrUndefined(labelText)
        ? ((
            <span class={labelClasses.join(' ')} aria-hidden="true" id={`ads:checkbox:${this.identifier}:label`}>
              {labelText}
            </span>
          ) as HTMLElement)
        : null,
      <span class="c-checkbox__cross" role="presentation">
        {generateSVGImage({ data: svgPathData, height, width })}
      </span>,
    ];

    if (isReversed) {
      $contentEls.reverse();
    }

    return (
      <div
        class={checkboxClasses.join(' ')}
        {...labelAttributes}
        onBlur={this.handleCheckboxBlur}
        onFocus={this.handleCheckboxFocus}
        onKeyDown={this.handleCheckboxKeyDown}
        onClick={this.handleCheckboxChange}
      >
        {$contentEls}
      </div>
    ) as VNode;
  }
  private getTooltipPosition = (isReversed: boolean) =>
    isReversed ? (!getIsElementRTL(this.el) ? Positions.LEFT : Positions.RIGHT) : !getIsElementRTL(this.el) ? Positions.RIGHT : Positions.LEFT;

  protected render(): VNode {
    const isReversed = this.el.classList.contains('is-reversed');

    // Generate the tooltip
    const $tooltipEl = !isNullOrUndefinedOrEmpty(this.tooltip)
      ? ((
          <ads-tooltip
            theme={this.theme}
            class="c-checkbox__tooltip"
            /* eslint-disable */
            onOpenChange={(evt) => ((evt.target as HTMLAdsTooltipElement).position = this.getTooltipPosition(isReversed))}
            /* eslint-enable */
            text={this.tooltip}
            position={this.getTooltipPosition(isReversed)}
          />
        ) as HTMLElement)
      : null;

    // Generate the helper text
    const helperLabel = !isNullOrUndefinedOrEmpty(this.helperText)
      ? ((
          <div class={`c-checkbox__helper ${this.el.classList.contains('is-small') ? 'is-small' : ''}`}>
            <label htmlFor={this.name}>{this.helperText}</label>
          </div>
        ) as HTMLElement)
      : null;

    const $elements = [this.renderContentContainer(isReversed), $tooltipEl, this.renderInputElement()];
    isReversed ? $elements.reverse() : null;

    return (
      <Fragment>
        <div id={`ads:checkbox:${this.identifier}`} class="c-checkbox">
          {$elements}
        </div>
        {helperLabel}
      </Fragment>
    ) as VNode;
  }
}
